import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'

interface OnboardingState {
  loading: boolean
  needsOnboarding: boolean
  needsPasswordSetup: boolean
  companyId: string | null
  companyActive: boolean
  /** ISO timestamp ou null. Quando setado, conta está em grace period de 30 dias antes do purge (LGPD). */
  deletionRequestedAt: string | null
  refresh: () => Promise<void>
  complete: () => Promise<void>
  completePasswordSetup: () => Promise<void>
}

export function useOnboarding(): OnboardingState {
  const [loading, setLoading] = useState(true)
  const [needsOnboarding, setNeedsOnboarding] = useState(false)
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(false)
  const [companyId, setCompanyId] = useState<string | null>(null)
  const [companyActive, setCompanyActive] = useState(true)
  const [deletionRequestedAt, setDeletionRequestedAt] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setLoading(false); return }
    const { data } = await supabase
      .from('profiles')
      .select('company_id, onboarded, password_set, deletion_requested_at, companies(active)')
      .eq('id', user.id)
      .maybeSingle()
    if (data) {
      setCompanyId(data.company_id as string)
      setNeedsOnboarding(!data.onboarded)
      setNeedsPasswordSetup(data.password_set === false)
      setDeletionRequestedAt((data.deletion_requested_at as string | null) ?? null)
      const co = data.companies as unknown as { active: boolean } | null
      const active = co?.active
      setCompanyActive(active !== false) // default true se coluna ainda não existir
    }
    setLoading(false)
  }, [])

  const complete = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    await supabase.from('profiles').update({ onboarded: true }).eq('id', user.id)
    setNeedsOnboarding(false)
  }, [])

  const completePasswordSetup = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    await supabase.from('profiles').update({ password_set: true }).eq('id', user.id)
    setNeedsPasswordSetup(false)
  }, [])

  useEffect(() => { refresh() }, [refresh])

  return { loading, needsOnboarding, needsPasswordSetup, companyId, companyActive, deletionRequestedAt, refresh, complete, completePasswordSetup }
}
